/**
 * createChatStore — central reactive state for the Team Chat UI.
 *
 * Built on @preact/signals. All ActionCable and REST events mutate signals
 * here; Preact components subscribe automatically via signal reads.
 *
 * Not a singleton — each chat_react_wrap_controller mount gets its own store
 * instance (float panel and fullscreen are independent).
 *
 * Signal shapes:
 *   Room:    { id, name, kind, avatarUrl, unread, lastMessage, dmPersonId? }
 *   Message: { id, roomId, body, creator_person: { id, name, avatar_url },
 *              createdAt, updatedAt, replyToId, replyTo?, attachments,
 *              _sendStatus?: 'sending'|'failed', _clientId? }
 *   Pending: Message with _sendStatus='sending' and _clientId for swap
 */

import { signal, computed, batch } from '@preact/signals'
import { CablePhase, RECEIPT_READ, RECEIPT_DELIVERED, genClientId, buildOptimistic, isRoomUnread } from '@tshmanager/chat-shared'

// Re-export for consumers that need the client-id / optimistic helpers
export { genClientId, buildOptimistic }

// Receipt kind constants re-exported from shared (avoids string literals in components)
export { RECEIPT_READ, RECEIPT_DELIVERED }

// Receipt buffer: roomId → Array of pending receipt_snapshot events received
// while the room's messages are still loading. Applied atomically after fetch.
const receiptBuffer = new Map()

/**
 * Mark a room as loading — receipt_snapshot events for this room should be
 * buffered instead of applied directly. Called by the fetch-messages logic.
 */
export function startReceiptBuffer(roomId) {
  if (!receiptBuffer.has(roomId)) {
    receiptBuffer.set(roomId, [])
  }
}

/**
 * Return buffered receipt events for a room and clear the buffer.
 * Returns null if the room was not being buffered.
 */
export function drainReceiptBuffer(roomId) {
  if (!receiptBuffer.has(roomId)) return null
  const events = receiptBuffer.get(roomId)
  receiptBuffer.delete(roomId)
  return events
}

/**
 * Check if a room is currently buffering receipt events (i.e. messages loading).
 */
export function isReceiptBuffering(roomId) {
  return receiptBuffer.has(roomId)
}

/**
 * Buffer a receipt_snapshot event for a room that is currently loading.
 * The cable module calls this when isReceiptBuffering returns true.
 */
export function bufferReceiptSnapshot(roomId, data) {
  const buf = receiptBuffer.get(roomId)
  if (buf) buf.push(data)
}

export function createChatStore({ roomId, personId, personName = '', tenantId, canManageRooms = false }) {
  // ── Rooms ────────────────────────────────────────────────────
  const rooms        = signal([])         // Room[] — full sidebar list
  const activeRoomId = signal(roomId || null)  // Number | null

  // ── Messages ─────────────────────────────────────────────────
  const messagesByRoom  = signal({})       // { [roomId]: Message[] } — oldest-first
  const hasMoreByRoom   = signal({})       // { [roomId]: Boolean }
  const oldestIdByRoom  = signal({})       // { [roomId]: Number } — pagination cursor
  const loadingRoomMsgs = signal(null)     // roomId currently being loaded (null = idle)
  const roomsLoading    = signal(true)     // true until first rooms list response arrives
  const roomsLoadError  = signal(null)     // string | null — failed rooms.list() message

  // ── UI state ──────────────────────────────────────────────────
  const replyTo        = signal(null)     // Message | null
  const pendingMsgs    = signal([])       // optimistic messages (_sendStatus: 'sending')
  const highlightMsgId = signal(null)     // reply-jump flash target (message id)
  const cablePhase     = signal(CablePhase.Idle)

  // ── Typing ────────────────────────────────────────────────────
  const typingByRoom = signal({})         // { [roomId]: [{personId, name, avatarUrl}] }

  // ── Presence ──────────────────────────────────────────────────
  const presenceMap = signal({})          // { [personId]: { online, lastActiveAt } }

  // ── Reactions & Read receipts ─────────────────────────────────
  const reactionsById    = signal({})      // { [messageId]: ReactionSummary[] }
  const receiptsById     = signal({})      // { [messageId]: { readBy: [], deliveredTo: [] } }
  const memberWatermarks = signal({})      // { [personId]: { person, lastReadAt, lastDeliveredAt } }

  // ── Float panels (dock) ───────────────────────────────────────
  const floatPanels = signal([])          // [{ roomId, minimized }] — localStorage synced

  // ── Contact profile panel ─────────────────────────────────────
  const profilePerson = signal(null)      // { id, name, avatar_url } | null — set by avatar click

  // ── Search ────────────────────────────────────────────────────
  const searchQuery   = signal('')        // in-conversation search string
  const searchMatches = signal([])        // matching message ids (Number[])
  const searchIdx     = signal(0)         // current match index

  // ── Computed ──────────────────────────────────────────────────

  // Pending + confirmed messages for the active room, pending first so
  // optimistic bubbles appear at the bottom before the server echo arrives.
  const currentMessages = computed(() => {
    const rid  = activeRoomId.value
    const msgs = messagesByRoom.value[rid] ?? []
    const mine = pendingMsgs.value.filter(m => (m.roomId ?? m.chat_room_id) === rid)
    return [...msgs, ...mine]
  })

  const typingNames = computed(() =>
    (typingByRoom.value[activeRoomId.value] ?? []).map(t => t.name)
  )

  const activeRoom = computed(() =>
    rooms.value.find(r => r.id === activeRoomId.value) ?? null
  )

  const totalUnread = computed(() =>
    rooms.value.reduce((sum, r) => sum + (isRoomUnread(r) ? (typeof r.unread === 'number' ? r.unread : 1) : 0), 0)
  )

  return {
    // Identity (read-only, set at construction time)
    personId,
    personName,
    tenantId,
    canManageRooms: !!canManageRooms,

    // Signals
    rooms,
    activeRoomId,
    messagesByRoom,
    hasMoreByRoom,
    oldestIdByRoom,
    loadingRoomMsgs,
    roomsLoading,
    roomsLoadError,
    replyTo,
    pendingMsgs,
    highlightMsgId,
    cablePhase,
    typingByRoom,
    presenceMap,
    reactionsById,
    receiptsById,
    memberWatermarks,
    floatPanels,
    profilePerson,
    searchQuery,
    searchMatches,
    searchIdx,

    // Computed
    currentMessages,
    typingNames,
    activeRoom,
    totalUnread,
  }
}
